import React from "react";
import { Link } from "react-router-dom";
import { Badge, formatCurrency, formatSquadInfo } from "../../../components/UI/DesignStandards";
import { canSelfServeCancel, canRequestRefund } from "../../../utility/signupAvailability";
import {
  bowlingCenterMapsHref,
  signupAdvertisedTotal,
} from "../../../utility/signupDashboard";
import { formatBowlerNames } from "../utils/signupUtils";
import { tournamentPath } from "../../../utility/tournamentUrls";

const paymentBadge = (signup) => {
  if (signup.status === "waitlisted") {
    return {
      label: signup.waitlistPosition
        ? `Waitlist · position ${signup.waitlistPosition}`
        : "Waitlist",
      variant: "warning",
    };
  }
  if (signup.status === "cancelled") {
    return { label: "Cancelled", variant: "default" };
  }
  if (signup.status === "denied") {
    return { label: "Denied", variant: "default" };
  }
  if (signup.paymentStatus === "paid" || signup.paymentStatus === "walk_up") {
    return { label: "Paid", variant: "success" };
  }
  if (signup.paymentStatus === "held") {
    return { label: "Held", variant: "default" };
  }
  if (signup.paymentStatus === "refunded") {
    return { label: "Refunded", variant: "default" };
  }
  return { label: "Due", variant: "primary" };
};

const MetaRow = ({ label, children }) => (
  <div className="grid grid-cols-[6.5rem_1fr] sm:grid-cols-[7.5rem_1fr] gap-x-3 text-sm">
    <dt className="text-brand-muted">{label}</dt>
    <dd className="text-brand-charcoal min-w-0 break-all">{children}</dd>
  </div>
);

const SignupCard = ({
  signup,
  onViewSignup,
  onCancelSignup,
  onRequestRefund,
  showCancel,
  showRequestRefund: showRequestRefundProp,
  companionNote = "",
}) => {
  const canCancel = showCancel ?? canSelfServeCancel(signup);
  const showRequestRefund = showRequestRefundProp ?? canRequestRefund(signup);
  const status = paymentBadge(signup);
  const bowlingCenter = signup.squad?.bowlingCenters?.centerName;
  const mapsHref = bowlingCenterMapsHref(signup.squad?.bowlingCenters);
  const tournamentId = signup.tournament?.id;
  const tournamentSlug = signup.tournament?.slug || tournamentId;
  const tournamentName = signup.tournament?.name || "Tournament";
  const bowlers = formatBowlerNames(signup.signupBowlers);

  return (
    <li className="p-5 sm:p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm text-brand-muted">
            {tournamentId ? (
              <Link
                to={tournamentPath(tournamentSlug)}
                className="text-sm text-brand-gold-ink hover:underline"
              >
                {tournamentName}
              </Link>
            ) : (
              tournamentName
            )}
          </p>
          <h2 className="font-display font-bold text-brand-charcoal tracking-tight">
            {signup.tournamentEvents?.eventName || "Entry"}
          </h2>
          <div className="mt-2 flex flex-wrap gap-2">
            <Badge variant={status.variant}>{status.label}</Badge>
            {signup.refundRequested ? (
              <Badge variant="warning">Refund requested</Badge>
            ) : null}
            {signup.disputed ? (
              <Badge variant="danger">Disputed</Badge>
            ) : null}
          </div>
        </div>
        <div className="font-score text-lg font-display font-bold text-brand-charcoal tabular-nums shrink-0">
          {formatCurrency(signupAdvertisedTotal(signup))}
        </div>
      </div>

      <dl className="mt-4 space-y-1.5">
        <MetaRow label="Squad">
          {formatSquadInfo(signup.squad?.squadDate, signup.squad?.squadTime)}
        </MetaRow>
        {bowlingCenter ? (
          <MetaRow label="Bowling center">
            {mapsHref ? (
              <a
                href={mapsHref}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-gold-ink hover:underline"
              >
                {bowlingCenter}
              </a>
            ) : (
              bowlingCenter
            )}
          </MetaRow>
        ) : null}
        {signup.heldUntil &&
        (signup.status === "held" || signup.paymentStatus === "held") ? (
          <MetaRow label="Hold ends">
            {new Date(signup.heldUntil).toLocaleString()}
          </MetaRow>
        ) : null}
        <MetaRow label={bowlers.includes(",") ? "Bowlers" : "Bowler"}>
          {bowlers || "—"}
        </MetaRow>
      </dl>

      <div className="mt-4 flex flex-col sm:flex-row sm:flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onViewSignup(signup)}
          className="inline-flex items-center justify-center min-h-11 px-4 rounded-lg border border-brand-charcoal/20 text-sm font-display font-bold text-brand-charcoal hover:border-brand-charcoal"
        >
          View details
        </button>
        {canCancel ? (
          <button
            type="button"
            onClick={() => onCancelSignup(signup)}
            className="inline-flex items-center justify-center min-h-11 px-4 rounded-lg bg-red-600 text-sm font-display font-bold text-white hover:bg-red-700"
          >
            Cancel
          </button>
        ) : null}
        {companionNote ? (
          <p className="text-sm text-brand-muted sm:self-center">{companionNote}</p>
        ) : null}
        {showRequestRefund ? (
          <button
            type="button"
            onClick={() => onRequestRefund(signup)}
            className="inline-flex items-center justify-center min-h-11 px-4 rounded-lg border border-brand-charcoal/20 text-sm font-display font-bold text-brand-charcoal hover:border-brand-charcoal"
          >
            Request a refund
          </button>
        ) : null}
      </div>
    </li>
  );
};

export default SignupCard;
